
import { useState } from "react";
import { aiDebugState } from "@/utils/aiUtils";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Copy, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

const AIDebugPanel = () => {
  const [isOpen, setIsOpen] = useState(false);

  const handleCopyToClipboard = (text: string, type: string) => {
    navigator.clipboard.writeText(text);
    toast.success(`${type} copied to clipboard`);
  };

  if (!isOpen && (!aiDebugState.lastPrompt || !aiDebugState.lastResponse)) {
    return (
      <div className="flex justify-center mt-4">
        <Button 
          variant="outline" 
          size="sm" 
          onClick={() => setIsOpen(true)}
          disabled={!aiDebugState.lastPrompt && !aiDebugState.lastResponse}
        >
          <Eye className="h-4 w-4 mr-1" /> Show AI Debug Panel
        </Button>
      </div>
    );
  }

  const formatTimestamp = (timestamp: string) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  return (
    <Card className="mt-6">
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-lg">AI Processing Debug</CardTitle>
        <Button variant="ghost" size="sm" onClick={() => setIsOpen(!isOpen)}>
          {isOpen ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          {isOpen ? " Hide Details" : " Show Details"}
        </Button>
      </CardHeader>
      
      {isOpen && (
        <CardContent>
          <div className="text-sm text-gray-500 mb-3 space-y-1">
            {aiDebugState.responseTime > 0 && (
              <p>Response time: {(aiDebugState.responseTime / 1000).toFixed(2)} seconds</p>
            )}
            {aiDebugState.timestamp && (
              <p>Timestamp: {formatTimestamp(aiDebugState.timestamp)}</p>
            )}
            {aiDebugState.model && (
              <p>Model: {aiDebugState.model}</p>
            )}
          </div>
          
          <Tabs defaultValue="prompt">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="prompt">Prompt Sent to OpenAI</TabsTrigger>
              <TabsTrigger value="response">Response from OpenAI</TabsTrigger>
            </TabsList>
            
            <TabsContent value="prompt" className="mt-4">
              <div className="flex justify-end mb-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => handleCopyToClipboard(aiDebugState.lastPrompt, "Prompt")}
                >
                  <Copy className="h-3 w-3 mr-1" /> Copy
                </Button>
              </div>
              <Textarea 
                value={aiDebugState.lastPrompt} 
                readOnly 
                className="h-[300px] font-mono text-xs"
              />
            </TabsContent>
            
            <TabsContent value="response" className="mt-4">
              <div className="flex justify-end mb-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => handleCopyToClipboard(aiDebugState.lastResponse, "Response")}
                >
                  <Copy className="h-3 w-3 mr-1" /> Copy
                </Button>
              </div>
              <Textarea 
                value={aiDebugState.lastResponse} 
                readOnly 
                className="h-[300px] font-mono text-xs"
              />
            </TabsContent>
          </Tabs>
        </CardContent>
      )}
    </Card>
  );
};

export default AIDebugPanel;
